import { useState, useMemo } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { fr } from "date-fns/locale";
import { format } from "date-fns";
import { CheckCircle2, CalendarIcon, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { SITE, SERVICE_OPTIONS, TIME_SLOTS } from "@/lib/siteData";

export default function Appointment() {
  const [date, setDate] = useState(null);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({
    name: "", phone: "", email: "", vehicle: "", plate: "", service: "", message: "",
  });

  // Disable Sun (0) and Mon (1), past dates
  const disabledDays = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [
      { before: today },
      { dayOfWeek: [0, 1] }, // Dimanche, Lundi
    ];
  }, []);

  const formattedDate = date ? format(date, "EEEE d MMMM yyyy", { locale: fr }) : "";

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!date) {
      toast.error("Veuillez sélectionner une date");
      return;
    }
    if (!slot) {
      toast.error("Veuillez sélectionner un créneau horaire");
      return;
    }
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Veuillez remplir au minimum votre nom et téléphone");
      return;
    }
    if (!form.service) {
      toast.error("Veuillez sélectionner un service");
      return;
    }

    const subject = `Nouvelle demande de RDV - ${form.name} - ${formattedDate} à ${slot}`;
    const body = `Bonjour,

Une nouvelle demande de rendez-vous vient d'être effectuée sur le site Sphère Auto.

──────────────────────────────────
RENDEZ-VOUS SOUHAITÉ
Date : ${formattedDate}
Heure : ${slot}

──────────────────────────────────
COORDONNÉES CLIENT
Nom : ${form.name}
Téléphone : ${form.phone}
Email : ${form.email || "(non renseigné)"}

──────────────────────────────────
VÉHICULE & PRESTATION
Véhicule : ${form.vehicle || "(non renseigné)"}
Plaque d'immatriculation : ${form.plate || "(non renseignée)"}
Service demandé : ${form.service}

──────────────────────────────────
MESSAGE
${form.message || "(aucun message)"}

──────────────────────────────────
Merci de recontacter le client pour confirmer le rendez-vous.

Sphère Auto - ${SITE.address.full}
`;

    const mailto = `mailto:${SITE.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;

    toast.success("Votre messagerie va s'ouvrir avec la demande pré-remplie. Envoyez l'email pour confirmer votre RDV !", {
      duration: 6000,
    });
  };

  return (
    <section id="appointment" className="py-20 lg:py-32 bg-slate-900 relative overflow-hidden" data-testid="appointment-section">
      <div className="absolute inset-0 bg-grain opacity-50" />
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" />
      <div className="absolute -top-32 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full mb-5">
            <CalendarIcon className="w-4 h-4 text-blue-400" />
            <span className="text-xs font-semibold text-blue-300 uppercase tracking-wider">Prise de rendez-vous en ligne</span>
          </div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight">
            Réservez votre créneau en <span className="text-blue-400">2 minutes</span>
          </h2>
          <p className="mt-5 text-base lg:text-lg text-slate-300">
            Choisissez votre date et votre horaire. Nous vous recontactons pour confirmer.
          </p>
        </div>

        <Card className="bg-white border-0 shadow-2xl rounded-2xl overflow-hidden">
          <form onSubmit={handleSubmit} className="grid lg:grid-cols-2" data-testid="appointment-form">
            {/* Left: Calendar + Time slots */}
            <div className="p-6 lg:p-8 bg-slate-50 border-r border-slate-200">
              <div className="mb-6">
                <Label className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
                  <CalendarIcon className="w-4 h-4 text-blue-600" />
                  1. Choisissez une date
                </Label>
                <div className="bg-white rounded-xl border border-slate-200 p-2 flex justify-center">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={disabledDays}
                    locale={fr}
                    weekStartsOn={1}
                    className="rounded-md"
                    data-testid="appointment-calendar"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">Ouvert du mardi au samedi, 9h-18h.</p>
              </div>

              <div>
                <Label className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
                  <Clock className="w-4 h-4 text-blue-600" />
                  2. Sélectionnez un créneau
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setSlot(t)}
                      disabled={!date}
                      className={`py-2.5 px-2 text-sm font-semibold rounded-lg border transition-all ${
                        slot === t
                          ? "bg-blue-600 text-white border-blue-600 shadow-md"
                          : "bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:text-blue-600 disabled:opacity-40 disabled:cursor-not-allowed"
                      }`}
                      data-testid={`time-slot-${t}`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
                {date && slot && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2" data-testid="appointment-summary">
                    <CheckCircle2 className="w-4 h-4 text-blue-600 flex-shrink-0" />
                    <div className="text-sm text-blue-900">
                      <span className="font-semibold capitalize">{formattedDate}</span> à <span className="font-semibold">{slot}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Form fields */}
            <div className="p-6 lg:p-8 bg-white">
              <Label className="text-sm font-semibold text-slate-900 mb-4 block">3. Vos coordonnées</Label>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-xs text-slate-600 mb-1.5">Nom & prénom *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Jean Dupont"
                      data-testid="appointment-input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-xs text-slate-600 mb-1.5">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => handleChange("phone", e.target.value)}
                      placeholder="06 12 34 56 78"
                      data-testid="appointment-input-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs text-slate-600 mb-1.5">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    placeholder="vous@exemple.com"
                    data-testid="appointment-input-email"
                  />
                </div>

                <div>
                  <Label htmlFor="vehicle" className="text-xs text-slate-600 mb-1.5">Véhicule (marque / modèle / année)</Label>
                  <Input
                    id="vehicle"
                    value={form.vehicle}
                    onChange={(e) => handleChange("vehicle", e.target.value)}
                    placeholder="Renault Clio 2019"
                    data-testid="appointment-input-vehicle"
                  />
                </div>

                <div>
                  <Label htmlFor="plate" className="text-xs text-slate-600 mb-1.5">Plaque d&apos;immatriculation</Label>
                  <Input
                    id="plate"
                    value={form.plate}
                    onChange={(e) => handleChange("plate", e.target.value.toUpperCase())}
                    placeholder="AB-123-CD"
                    maxLength={10}
                    className="uppercase tracking-wider font-semibold"
                    data-testid="appointment-input-plate"
                  />
                </div>

                <div>
                  <Label htmlFor="service" className="text-xs text-slate-600 mb-1.5">Service souhaité *</Label>
                  <Select value={form.service} onValueChange={(v) => handleChange("service", v)}>
                    <SelectTrigger id="service" data-testid="appointment-select-service">
                      <SelectValue placeholder="Choisir un service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="message" className="text-xs text-slate-600 mb-1.5">Message (description, symptômes...)</Label>
                  <Textarea
                    id="message"
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    placeholder="Bruit au freinage, voyant moteur allumé..."
                    rows={3}
                    data-testid="appointment-textarea-message"
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-6 text-base rounded-lg group transition-all hover:scale-[1.02]"
                  data-testid="appointment-submit-button"
                >
                  <Send className="w-4 h-4 mr-2 group-hover:translate-x-0.5 transition-transform" />
                  Envoyer ma demande de RDV
                </Button>

                <p className="text-xs text-slate-500 text-center leading-relaxed">
                  Votre messagerie s&apos;ouvrira avec la demande pré-remplie. Un email sera envoyé à <span className="font-semibold">{SITE.email}</span>.
                </p>
              </div>
            </div>
          </form>
        </Card>
      </div>
    </section>
  );
}
